import type { SafetyFunctionWithArchitecture } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { MoreHorizontal, PencilIcon, TrashIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PlrBadge } from './PlrBadge'
import {
  countSistemaArchitecture,
  SistemaBlockDiagramThumbnail,
} from './SistemaBlockDiagram'

type SafetyFunctionOverviewCardProps = {
  safetyFunction: SafetyFunctionWithArchitecture
  onRename: (safetyFunction: SafetyFunctionWithArchitecture) => void
  onDelete: (safetyFunctionId: string) => void
  isMutationPending: boolean
}

export function SafetyFunctionOverviewCard({
  safetyFunction,
  onRename,
  onDelete,
  isMutationPending,
}: SafetyFunctionOverviewCardProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const navigate = useNavigate()
  const counts = countSistemaArchitecture(safetyFunction)

  const openDetail = () => {
    navigate(safetyFunction.id)
  }

  return (
    <Card
      className='cursor-pointer transition-colors hover:border-primary/50'
      onClick={openDetail}
    >
      <CardHeader className='flex flex-row items-start justify-between gap-2 space-y-0'>
        <div className='min-w-0 space-y-1.5'>
          <CardTitle className='flex flex-wrap items-center gap-2'>
            <span className='truncate'>{safetyFunction.name}</span>
            {safetyFunction.sfid != null &&
            safetyFunction.sfid.trim() !== '' ? (
              <Badge variant='secondary' className='font-normal'>
                {safetyFunction.sfid}
              </Badge>
            ) : null}
            {safetyFunction.source === 'EPLAN' ? (
              <Badge variant='outline' className='font-normal'>
                {t('safetyFunctionsPage.card.sourceEplan')}
              </Badge>
            ) : null}
          </CardTitle>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
              {t('safetyFunctionsPage.card.plrLabel')}
              <PlrBadge plr={safetyFunction.plr} />
            </span>
            <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
              {t('safetyFunctionsPage.card.plLabel')}
              <PlrBadge plr={safetyFunction.plcal} />
            </span>
            {safetyFunction.source === 'EPLAN' &&
            safetyFunction.eplanReferenceId != null &&
            safetyFunction.eplanReferenceId.trim() !== '' ? (
              <span className='text-xs text-muted-foreground'>
                {t('safetyFunctionsPage.card.eplanReferenceId', {
                  referenceId: safetyFunction.eplanReferenceId,
                })}
              </span>
            ) : null}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
            <Button
              variant='ghost'
              size='icon'
              className='size-8 shrink-0'
              disabled={isMutationPending}
            >
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='end'
            onClick={(event) => event.stopPropagation()}
          >
            <DropdownMenuItem onClick={openDetail}>
              {t('safetyFunctionsPage.actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRename(safetyFunction)}>
              <PencilIcon />
              {t('safetyFunctionsPage.actions.rename')}
            </DropdownMenuItem>
            <DropdownMenuItem
              variant='destructive'
              className='text-destructive focus:text-destructive'
              onClick={() => onDelete(safetyFunction.id)}
            >
              <TrashIcon />
              {t('safetyFunctionsPage.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className='space-y-3'>
        {safetyFunction.description != null &&
        safetyFunction.description.trim().length > 0 ? (
          <p className='line-clamp-2 text-sm text-muted-foreground'>
            {safetyFunction.description}
          </p>
        ) : null}

        <SistemaBlockDiagramThumbnail safetyFunction={safetyFunction} />

        <div className='flex flex-wrap gap-1.5 text-xs text-muted-foreground'>
          <Badge variant='outline' className='font-normal'>
            {t('safetyFunctionsPage.card.subsystemCount', {
              count: counts.components,
            })}
          </Badge>
          <Badge variant='outline' className='font-normal'>
            {t('safetyFunctionsPage.card.channelCount', {
              count: counts.channels,
            })}
          </Badge>
          <Badge variant='outline' className='font-normal'>
            {t('safetyFunctionsPage.card.blockCount', {
              count: counts.blocks,
            })}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}
